import { useEffect, useState } from 'react'

const REVIEWS_SELECTOR = '[data-testid="homepageReviewsSection"]'
const FIRST_BOX_SELECTOR = '[data-testid="first-box-section"]'

// Show once the Reviews section's top edge has scrolled up to the viewport's vertical centre;
// hide once the First Box section's top edge has entered the viewport. Both anchors are read
// fresh on every call so scrolling up reverses the same boundaries with no separate "scrolling
// up" branch.
//
// Uses the section's top edge, not its own vertical centre — the Reviews section's DOM node
// spans its heading, the Trustpilot widget, the review cards, a recipe-video carousel, and an
// "As Featured In" press strip at the very end, so the section's own midpoint falls well past
// the review cards (down around the video carousel/press strip). Triggering off the top edge
// instead shows the bar as soon as the review cards themselves are approaching view.
const computeVisibility = () => {
  const reviews = document.querySelector(REVIEWS_SELECTOR)
  const firstBox = document.querySelector(FIRST_BOX_SELECTOR)

  if (!reviews || !firstBox) {
    return false
  }

  const reviewsRect = reviews.getBoundingClientRect()
  const firstBoxRect = firstBox.getBoundingClientRect()

  const hasReachedReviews = reviewsRect.top <= window.innerHeight / 2
  const hasReachedFirstBox = firstBoxRect.top <= window.innerHeight

  return hasReachedReviews && !hasReachedFirstBox
}

export function useFloatingCTAVisibility() {
  // Starts false, not computeVisibility() — this hook runs during SSR (renderToString), where
  // `document` doesn't exist, and a `useState` lazy initializer runs synchronously on every
  // render, server included. Calling computeVisibility() here would throw
  // `ReferenceError: document is not defined` and crash the server render for the whole page
  // whenever a visitor is bucketed into the CwC homepage treatment. The real value is computed
  // client-only, in the effect below (effects never run during SSR).
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const handleScroll = () => setIsVisible(computeVisibility())

    // Computed on mount so a visitor landing already scrolled mid-page (anchor link, browser
    // scroll restoration) gets the correct value even if the DOM wasn't fully laid out yet.
    handleScroll()

    window.addEventListener('scroll', handleScroll, { passive: true })

    // The thresholds above read window.innerHeight fresh on every call, but resizing the viewport
    // itself (e.g. a desktop user dragging the window shorter/taller) doesn't fire a scroll event
    // and may not change document.body's own box size either, so it wouldn't otherwise trigger a
    // recompute — the state would stay stale against the new viewport height until the next scroll.
    window.addEventListener('resize', handleScroll, { passive: true })

    // Async content landing between the two anchors (e.g. the Weekly Menu section, which mounts
    // once its own data fetch resolves) shifts First Box down without firing a scroll event, which
    // could otherwise leave a stale visibility value until the next scroll. A ResizeObserver on
    // the document body recomputes whenever the page's overall layout height changes for any
    // reason — not just this one section — so this stays correct as more async content lands.
    const resizeObserver = new ResizeObserver(handleScroll)
    resizeObserver.observe(document.body)

    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleScroll)
      resizeObserver.disconnect()
    }
  }, [])

  return isVisible
}
